import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { AccountReportsTabs, NavigationBreadcrumbs } from "../components/ui";
import { CompanyInfo } from "../components/features/CompanyInfo";
import { useParams } from "react-router-dom";
import {
  useAppSelector,
  useNotFoundRedirect,
  useStateWithSearchParams,
} from "../hooks";
import { useGetCampaignDetailsQuery, useGetCampaignSummaryQuery } from "../api";
import { AppRoutes } from "../types/enums";
import { AccountReportsPage, AccountPage } from "../pages";

export const AccountLayout = () => {
  const { isEditor } = useAppSelector((state) => state.company.userRoles);
  const { account_id, campaign_id } = useParams();

  const { searchDict } = useStateWithSearchParams<{
    tab?: string;
  }>({
    tab: undefined,
  });

  const { data, error } = useGetCampaignDetailsQuery(
    { account_id: account_id! },
    { skip: !account_id, refetchOnMountOrArgChange: true },
  );

  const { data: summary } = useGetCampaignSummaryQuery(
    { campaign_id: campaign_id! },
    { skip: !campaign_id },
  );

  useNotFoundRedirect({ error });

  if (!campaign_id || !data || !summary) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
      >
        <CircularProgress size={80} sx={{ color: "#FE8836", flexShrink: 1 }} />
      </Box>
    );
  }

  const {
    product_name,
    total_members,
    total_members_with_playbook,
    output: {
      company: { industry, name, size, product_url, buyer_journey },
    },
    has_account_plan,
    has_committee_changes,
    is_analyzed,
  } = data;

  const breadcrumbs = [
    {
      title: product_name,
      path: `${AppRoutes.CAMPAINGS}/${campaign_id}`,
    },
    {
      title: "Accounts",
      path: `${AppRoutes.ACCOUNTS_LIST}/${campaign_id}`,
    },
    {
      title: name,
    },
  ];

  return (
    <Stack
      sx={{ px: { sm: 8, md: 8, lg: 10, xl: 20 }, pt: 4, pb: 20, gap: "24px" }}
    >
      <Stack gap="16px">
        <Stack pb="8px">
          <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
        </Stack>
        <Box
          sx={{
            flexDirection: "row",
            display: "flex",
            gap: 1,
          }}
        >
          <Typography
            variant="h4"
            color="custom.textDarkBlue"
            sx={{ fontSize: "16px", lineHeight: "24px", fontWeight: 500 }}
          >
            General Information
          </Typography>
        </Box>
        <Stack gap={"8px"}>
          <CompanyInfo
            account_id={account_id!}
            campaign_id={campaign_id}
            name={name}
            size={size}
            industry={industry}
            product_url={product_url}
            contactsImported={total_members}
            isIntegration={!!summary?.crm}
            isDemo={summary?.is_demo}
            buyer_journey={buyer_journey}
          />
          <AccountReportsTabs
            isNeedUpdate={has_committee_changes}
            isDisabled={
              (!is_analyzed && total_members === 0) ||
              (!isEditor && !summary.has_playbooks)
            }
          />
        </Stack>
      </Stack>

      {searchDict.tab === "reports" ? (
        <AccountReportsPage
          hasAccountPlan={has_account_plan}
          isHideAccountPlan={!is_analyzed}
          isHidePlaybook={
            total_members === 0 ||
            (!isEditor && total_members_with_playbook === 0)
          }
        />
      ) : (
        <AccountPage />
      )}
    </Stack>
  );
};
